import { Button, Typography } from "@bracket/ui";
import { useWallet, } from "@store";
import { type ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { useAccount } from "wagmi";
import { API_URL, COUNTRY_BLACK_LIST, WHITE_ACCTS, } from "./const";


export type LocationResponse = {
	"CloudFront-Viewer-Country": string;
	"CloudFront-Viewer-Country-Name": string;
	"CloudFront-Viewer-Postal-Code"?: string;
};

export async function canAccessApp(address: string): Promise<{
	allowed: boolean;
	reason?: string;
}> {
	try {

		if([
			"localhost",
			"staging.bracket.fi",
			"canary.bracket.fi"
		].includes(globalThis.location.hostname)) {
			return { allowed: true, };
		}

		if(globalThis.location.hostname === "localhost") {
			return { allowed: true, };
		}

		if(globalThis.location.hostname === "staging.bracket.fi") {
			return { allowed: true, };
		}

		if(address && import.meta.env.VITE_ANON_WALLET_ADDR && address.toLowerCase() === import.meta.env.VITE_ANON_WALLET_ADDR.toLowerCase()) {
			return { allowed: true, };
		}


		if (
			WHITE_ACCTS.includes(address.toLowerCase()) &&
			!window.location.host.startsWith("localhost")
		) {
			return { allowed: true };
		}

		const response = await fetch(`${API_URL}/echo?account=${address}`, {
			method: "GET",
			headers: {
				Accept: "application/json",
				"x-api-key": import.meta.env.VITE_APP_X_API_KEY,
			},
		});

		if (!response.ok) {
			throw new Error("Failed to fetch location data");
		}

		const locationData = (await response.json()) as LocationResponse;
		const countryCode = locationData["CloudFront-Viewer-Country"];

		if(!countryCode) {
			throw new Error("Unable to determine location");
		}

		if(COUNTRY_BLACK_LIST.includes(countryCode)) {
			return {
				allowed: false,
				reason: `Access not allowed from ${locationData["CloudFront-Viewer-Country-Name"]}`,
			};
		}

		// Special case for Ukraine specific regions
		if(countryCode === "UA") {
			const postalCode = locationData["CloudFront-Viewer-Postal-Code"]
				? parseInt(locationData["CloudFront-Viewer-Postal-Code"], 10)
				: -1;

			if (
				(postalCode >= 83000 && postalCode <= 87999) ||
				(postalCode >= 91 && postalCode <= 95)
			) {
				return {
					allowed: false,
					reason: "Access not allowed from this region",
				};
			}
		}

		return { allowed: true };

	} catch (error) {
		console.error("Access check failed:", error);
		return {
			allowed: false,
			reason: "Unable to verify access eligibility",
		};
	}
}

interface AccessControlProviderProps {
	children: ReactNode;
}

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999999;
  background: var(--page-background);
  width: 100vw;
  height: 100vh;
`;

const RestrictedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
  gap: 0.5rem;

  > * + * {
    margin-top: 1rem;
  }
`;

export function AccessControlProvider({
	children,
}: AccessControlProviderProps) {

	const { address } = useAccount();
	const [accessState, setAccessState] = useState<{
		loading: boolean;
		allowed: boolean;
		reason?: string;
	}>({
		loading: true,
		allowed: false,
	});

	const { trm_pass } = useWallet();

	useEffect(() => {

		async function checkAccess() {

			setAccessState({ loading: true, allowed: false });

			if(!address) {
				setAccessState({ loading: false, allowed: true });
				return;
			}

			// const result = await canAccessApp(address);
			const result = { allowed: true, reason: undefined };

			setAccessState({
				loading: false,
				allowed: result.allowed,
				reason: result.reason,
			});
		}

		checkAccess();
	}, [address]);

	return (
		<>
			{children}
			{(accessState.loading || !accessState.allowed) && (
				<OverlayContainer>
					{accessState.loading ? (
						// <Loader center scheme="light" />
						children
					) : (
						<RestrictedContainer>
							<Typography variant="h2">Region Restriction</Typography>
							<Typography variant="paragraphLG" color="var(--text-subheading)">
								Sorry! your region{" "}
								{accessState.reason ? `[${accessState.reason}]` : ""} is not
								being supported by Bracket at the current time.
							</Typography>
							<Button
								variant="primary"
								onClick={() => window.open("https://www.bracket.fi", "_self")}
								label="Return Home"
							/>
						</RestrictedContainer>
					)}
				</OverlayContainer>
			)}
			{!trm_pass && (
				<OverlayContainer>
						<RestrictedContainer>
							<Typography variant="h2">Wallet Screen Failed</Typography>
							<Typography variant="paragraphLG" color="var(--text-subheading)">
								Sorry! Your wallet have been flagged by TRM as high risk and access is denied.
							</Typography>
							<Button
								variant="primary"
								onClick={() => window.open("https://www.bracket.fi", "_self")}
								label="Return Home"
							/>
						</RestrictedContainer>
				</OverlayContainer>
			)}
		</>
	);
}
